/**
 * Token Converter — consolidated from SPA and shared-logic implementations.
 *
 * Converts color palettes to DTCG (Design Token Community Group) format.
 * Supports multiple naming conventions, semantic token generation,
 * alias generation, and validation.
 */
// ── Helpers ─────────────────────────────────────────────────────────────────
function hexToChannels(hex) {
    const clean = hex.replace('#', '');
    let r, g, b;
    if (clean.length === 3) {
        r = parseInt(clean[0] + clean[0], 16) / 255;
        g = parseInt(clean[1] + clean[1], 16) / 255;
        b = parseInt(clean[2] + clean[2], 16) / 255;
    }
    else {
        r = parseInt(clean.substring(0, 2), 16) / 255;
        g = parseInt(clean.substring(2, 4), 16) / 255;
        b = parseInt(clean.substring(4, 6), 16) / 255;
    }
    return [r, g, b];
}
function hexToColorValue(hex) {
    const clean = hex.replace('#', '');
    const channels = hexToChannels(hex);
    const a = clean.length === 8 ? parseInt(clean.substring(6, 8), 16) / 255 : undefined;
    return { colorSpace: 'srgb', channels, alpha: a !== undefined && a < 1 ? a : undefined };
}
function calculateLuminance(hex) {
    const [r, g, b] = hexToChannels(hex);
    return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}
function getHueDescription(hex) {
    const [r, g, b] = hexToChannels(hex);
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    const delta = max - min;
    if (delta === 0)
        return null;
    let hue;
    if (max === r)
        hue = ((g - b) / delta) % 6;
    else if (max === g)
        hue = (b - r) / delta + 2;
    else
        hue = (r - g) / delta + 4;
    hue = Math.round(hue * 60);
    if (hue < 0)
        hue += 360;
    if ((hue >= 0 && hue < 30) || (hue >= 330 && hue <= 360))
        return 'red';
    if (hue >= 30 && hue < 60)
        return 'orange';
    if (hue >= 60 && hue < 90)
        return 'yellow';
    if (hue >= 90 && hue < 150)
        return 'green';
    if (hue >= 150 && hue < 210)
        return 'cyan';
    if (hue >= 210 && hue < 270)
        return 'blue';
    if (hue >= 270 && hue < 330)
        return 'purple';
    return null;
}
function slugify(text) {
    return text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
}
function convertNamingConvention(name, convention) {
    // Normalize aliases
    const normalized = convention === 'kebab' ? 'kebab-case'
        : convention === 'camel' ? 'camelCase'
            : convention === 'pascal' ? 'camelCase' // pascal handled below
                : convention === 'snake' ? 'snake_case'
                    : convention;
    switch (normalized) {
        case 'camelCase':
            if (convention === 'pascal') {
                return name.replace(/(^|-)([a-z])/g, (_, _s, c) => c.toUpperCase());
            }
            return name.replace(/-([a-z])/g, (_, c) => c.toUpperCase());
        case 'snake_case':
            return name.replace(/-/g, '_');
        case 'kebab-case':
            return name;
        default:
            return name;
    }
}
function generateTokenName(index, hex, convention) {
    if (convention === 'semantic') {
        const luminance = calculateLuminance(hex);
        let name = '';
        if (luminance > 0.8)
            name = 'very-light';
        else if (luminance > 0.6)
            name = 'light';
        else if (luminance > 0.4)
            name = 'medium';
        else if (luminance > 0.2)
            name = 'dark';
        else
            name = 'very-dark';
        const hue = getHueDescription(hex);
        if (hue)
            name += `-${hue}`;
        if (name === 'medium')
            name += `-${index + 1}`;
        return name;
    }
    if (convention === 'systematic') {
        return `${index + 1}`;
    }
    return convertNamingConvention(`color-${index + 1}`, convention);
}
// ── Public API: Simple (backward-compatible) ────────────────────────────────
/**
 * Simple palette-to-tokens conversion (backward-compatible).
 */
export function convertPaletteToTokens(colors, namingConvention = 'kebab') {
    const tokens = {};
    for (const color of colors) {
        const key = convertNamingConvention(color.name, namingConvention);
        tokens[key] = {
            $type: 'color',
            $value: { colorSpace: 'srgb', channels: hexToChannels(color.hex) },
            $description: `${color.name} color`,
        };
    }
    return tokens;
}
// ── Public API: Rich DTCG generation ────────────────────────────────────────
/**
 * Generate a full DTCG token set from a palette with semantic tokens and aliases.
 */
export function generateTokenSet(colors, names, options = {}) {
    const { namingConvention = 'systematic', includeSemanticTokens = true, includeAliases = true, paletteName = 'Palette', } = options;
    const tokenSet = {
        $schema: 'https://design-tokens.github.io/community-group/format/',
        $metadata: {
            id: slugify(paletteName),
            name: paletteName,
            description: `Design tokens generated from ${paletteName} palette`,
            version: '1.0.0',
            generated: new Date().toISOString(),
        },
        tokens: {},
    };
    // Color tokens
    const colorTokens = {};
    colors.forEach((hex, i) => {
        const tokenName = names[i] ? convertNamingConvention(names[i], namingConvention) : generateTokenName(i, hex, namingConvention);
        colorTokens[tokenName] = {
            $type: 'color',
            $value: hexToColorValue(hex),
            $description: `Color ${i + 1} from ${paletteName}`,
            $comment: `Original hex: ${hex}`,
        };
    });
    tokenSet.tokens.color = {
        $type: 'group',
        $description: `${paletteName} color palette tokens`,
        children: colorTokens,
    };
    // Semantic tokens
    if (includeSemanticTokens) {
        const semanticTokens = generateSemanticTokens(colors, names, namingConvention);
        if (Object.keys(semanticTokens).length > 0) {
            tokenSet.tokens.semantic = {
                $type: 'group',
                $description: `Semantic color tokens derived from ${paletteName}`,
                children: semanticTokens,
            };
        }
    }
    // Alias tokens
    if (includeAliases) {
        const aliasTokens = generateAliasTokens(colors, names, namingConvention);
        if (Object.keys(aliasTokens).length > 0) {
            tokenSet.tokens.aliases = {
                $type: 'group',
                $description: `Alias tokens for ${paletteName}`,
                children: aliasTokens,
            };
        }
    }
    return tokenSet;
}
function generateSemanticTokens(colors, names, convention) {
    const tokens = {};
    // Find lightest and darkest for semantic roles
    let lightestIdx = 0;
    let darkestIdx = 0;
    let maxLum = -1;
    let minLum = 2;
    colors.forEach((hex, i) => {
        const lum = calculateLuminance(hex);
        if (lum > maxLum) {
            maxLum = lum;
            lightestIdx = i;
        }
        if (lum < minLum) {
            minLum = lum;
            darkestIdx = i;
        }
    });
    if (maxLum > 0.7) {
        const tokenRef = names[lightestIdx] ? convertNamingConvention(names[lightestIdx], convention) : generateTokenName(lightestIdx, colors[lightestIdx], convention);
        tokens['background-primary'] = {
            $type: 'color',
            $value: `{color.${tokenRef}}`,
            $description: 'Primary background color',
        };
    }
    if (minLum < 0.3) {
        const tokenRef = names[darkestIdx] ? convertNamingConvention(names[darkestIdx], convention) : generateTokenName(darkestIdx, colors[darkestIdx], convention);
        tokens['text-primary'] = {
            $type: 'color',
            $value: `{color.${tokenRef}}`,
            $description: 'Primary text color',
        };
    }
    return tokens;
}
function generateAliasTokens(colors, names, convention) {
    const tokens = {};
    if (colors.length >= 1) {
        const ref = names[0] ? convertNamingConvention(names[0], convention) : generateTokenName(0, colors[0], convention);
        tokens.primary = { $type: 'color', $value: `{color.${ref}}`, $description: 'Primary color alias' };
    }
    if (colors.length >= 2) {
        const ref = names[1] ? convertNamingConvention(names[1], convention) : generateTokenName(1, colors[1], convention);
        tokens.secondary = { $type: 'color', $value: `{color.${ref}}`, $description: 'Secondary color alias' };
    }
    // Light/dark aliases
    const lightColors = colors.map((hex, i) => ({ hex, i, lum: calculateLuminance(hex) })).filter((c) => c.lum > 0.7).sort((a, b) => b.lum - a.lum);
    const darkColors = colors.map((hex, i) => ({ hex, i, lum: calculateLuminance(hex) })).filter((c) => c.lum < 0.3).sort((a, b) => a.lum - b.lum);
    if (lightColors.length > 0) {
        const idx = lightColors[0].i;
        const ref = names[idx] ? convertNamingConvention(names[idx], convention) : generateTokenName(idx, colors[idx], convention);
        tokens['text-on-dark'] = { $type: 'color', $value: `{color.${ref}}`, $description: 'Text color for dark backgrounds' };
    }
    if (darkColors.length > 0) {
        const idx = darkColors[0].i;
        const ref = names[idx] ? convertNamingConvention(names[idx], convention) : generateTokenName(idx, colors[idx], convention);
        tokens['text-on-light'] = { $type: 'color', $value: `{color.${ref}}`, $description: 'Text color for light backgrounds' };
    }
    return tokens;
}
/**
 * Validate a generated token set.
 */
export function validateTokenSet(tokenSet) {
    const errors = [];
    const warnings = [];
    if (!tokenSet.tokens || Object.keys(tokenSet.tokens).length === 0) {
        errors.push({ path: 'tokens', message: 'Token set must have tokens', code: 'MISSING_TOKENS' });
    }
    if (!tokenSet.$metadata?.name) {
        errors.push({ path: '$metadata.name', message: 'Token set must have a name', code: 'MISSING_METADATA_NAME' });
    }
    const colorGroup = tokenSet.tokens?.color;
    if (colorGroup?.children) {
        for (const [name, token] of Object.entries(colorGroup.children)) {
            if (token && typeof token === 'object' && '$type' in token) {
                const dt = token;
                if (dt.$type !== 'color') {
                    errors.push({ path: `tokens.color.${name}`, message: `Expected type "color", got "${dt.$type}"`, code: 'INVALID_TOKEN_TYPE' });
                }
            }
        }
    }
    return { valid: errors.length === 0, errors, warnings };
}
//# sourceMappingURL=tokenConverter.js.map